import React, { useState, useEffect, useMemo } from 'react';
import { useLayout } from '../context/LayoutContext';
import { 
  Box, 
  Typography, 
  Grid, 
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Button,
  Chip,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Breadcrumbs,
  Link,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton as MuiIconButton,
  FormControlLabel,
  Switch,
  Checkbox,
  RadioGroup,
  Radio
} from '@mui/material';
import { 
  ArrowBackIos,
  ArrowForwardIos,
  Today,
  Info,
  CalendarToday,
  Add,
  Edit,
  Delete,
  AccessTime,
  NavigateNext,
  WatchLater,
  DragIndicator,
  LocalHospital,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  useGetMonthlyAvailabilitiesQuery,
  useCreateAvailabilityMutation,
  useUpdateAvailabilityMutation,
} from '../slices/availabilityApiSlice';
import { getLocalDateString } from '../utils/dateUtils';

import { dublinLocalToUtcISO, dublinDateTimeInputStr } from '../utils/dublinTime';

// Shift time presets
const SHIFT_PRESETS = {
  'LD': { start: '08:00', end: '20:00', label: 'Long Day (8:00-20:00)' },
  'ND': { start: '20:00', end: '08:00', label: 'Night Duty (20:00-8:00)' },
  'AM': { start: '08:00', end: '16:00', label: 'Morning (8:00-16:00)' },
  'PM': { start: '14:00', end: '20:00', label: 'Afternoon (14:00-20:00)' },
  'TW': { start: '20:00', end: '00:00', label: 'Twilight (20:00-00:00)' },
  'ELD': { start: '07:30', end: '20:30', label: 'Extended Long Day (7:30-20:30)' },
  'ESCORT': { start: '08:00', end: '20:00', label: 'Escort (8:00-20:00)' },
};

// Unavailability reasons
const UNAVAILABILITY_REASONS = [
  'Annual Leave',
  'Adoptive Leave',
  'Bereavement Leave',
  'Carers Leave',
  'Certified Sick Leave',
  'Force Majeure',
  'Health and Safety Leave',
  'Jury Leave',
  'Maternity Leave',
  'Other',
  'Parents Leave',
  'Parental Leave',
  'Paternity Leave',
  'Sick Leave',
  'Study Leave / Mandatory Training',
];

// Color coding for different availability statuses
const AVAILABILITY_STATUS_COLORS = {
  pending: '#e3f2fd',
  approved: '#e8f5e8',
  rejected: '#ffebee',
  sickLeave: '#f44336',
  sickLeavePartial: '#ff9800',
  unavailable: '#d32f2f',
};

const scrollableTableStyle = {
  maxHeight: '600px',
  overflowY: 'auto',
  border: '1px solid #ddd',
  borderRadius: '8px',
  position: 'relative',
};

// Helper function to format hours and minutes
const formatHoursMinutes = (totalMinutes) => {
  if (!totalMinutes || totalMinutes === 0) return '0h 0m';
  
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  
  if (hours === 0) return `${minutes}m`;
  if (minutes === 0) return `${hours}h`;
  return `${hours}h ${minutes}m`;
};

// Helper function to format time for display
const formatTime = (dateTime) => {
  if (!dateTime) return '';
  const date = new Date(dateTime);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

// Helper function to format date for display
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};

// Helper function to get days in month
const getDaysInMonth = (year, month) => {
  return new Date(year, month + 1, 0).getDate();
};

// Month names
const monthNames = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

// Generate years (from current year - 1 to current year + 1)
const generateYears = () => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let i = currentYear - 1; i <= currentYear + 5; i++) {
    years.push(i);
  }
  return years;
};

const StaffAvailabilityViewStaff = () => {
  const { setFluid } = useLayout();
  const navigate = useNavigate();
  const { userInfo } = useSelector((state) => state.auth);
  
  const currentDate = new Date();
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth());
  const [calendarDays, setCalendarDays] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingAvailability, setEditingAvailability] = useState(null);
  
  // Selected days for multiple day entry
  const [selectedDays, setSelectedDays] = useState({
    monday: false,
    tuesday: false,
    wednesday: false,
    thursday: false,
    friday: false,
    saturday: false,
    sunday: false
  });

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // Form state - removed admin/manager specific fields
  const [formData, setFormData] = useState({
    startDateTime: '',
    endDateTime: '',
    comments: '',
    categoryOfStaff: userInfo?.jobtitle || '',
    wardDepartmentArea: userInfo?.ward || '',
    isUnavailable: false,
    unavailabilityReason: '',
    unavailabilityType: 'Full Day',
    unavailabilityDetails: '',
    isSickLeave: false,
    sickLeaveType: 'Full Day',
    sicknessReason: ''
  });
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Shift preset selection
  const [selectedPreset, setSelectedPreset] = useState('');
  
  // Drag and drop state
  const [draggingAvailability, setDraggingAvailability] = useState(null);
  const [dragOverDay, setDragOverDay] = useState(null);
  
  // Fetch monthly availabilities for the logged-in user
  const { 
    data: availabilitiesData, 
    isLoading: loadingAvailabilities,
    refetch: refetchAvailabilities,
    error: availabilitiesError
  } = useGetMonthlyAvailabilitiesQuery({
    year: selectedYear,
    month: selectedMonth + 1,
    staffId: userInfo?._id
  }, {
    skip: !userInfo?._id
  });
  
  // Mutations
  const [createAvailability, { isLoading: creating }] = useCreateAvailabilityMutation();
  const [updateAvailability, { isLoading: updating }] = useUpdateAvailabilityMutation();
  
  // Calculate month info
  const monthInfo = useMemo(() => {
    const daysInMonth = getDaysInMonth(selectedYear, selectedMonth);
    
    // Calculate the first day of the month
    const firstDayOfMonth = new Date(selectedYear, selectedMonth, 1);
    const firstDayOfWeek = firstDayOfMonth.getDay();
    
    // Calculate days from previous month to show (start from Monday)
    let daysFromPreviousMonth;
    if (firstDayOfWeek === 0) {
      daysFromPreviousMonth = 6; // Sunday -> show 6 days from previous month
    } else {
      daysFromPreviousMonth = firstDayOfWeek - 1; // Monday = 0, Tuesday = 1, etc.
    }
    
    // Calculate the calendar start date (first Monday shown)
    const calendarStartDate = new Date(firstDayOfMonth);
    calendarStartDate.setDate(calendarStartDate.getDate() - daysFromPreviousMonth);
    
    return {
      daysInMonth,
      monthName: monthNames[selectedMonth],
      year: selectedYear,
      calendarStartDate,
      daysFromPreviousMonth
    };
  }, [selectedYear, selectedMonth]);

  // Helper function to get the day name from a date
  const getDayNameFromDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    const day = date.getDay();
    return ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][day];
  };

  // Handle day toggle for multiple day selection
  const handleDayToggle = (day) => {
    setSelectedDays(prev => ({
      ...prev,
      [day]: !prev[day]
    }));
  };

  // Helper function to get the specific dates for selected days
  const getSelectedDatesInfo = () => {
    if (!formData.startDateTime) return [];
    
    const originalDate = new Date(formData.startDateTime);
    const originalDayIndex = originalDate.getDay();
    const dayNameMap = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    
    return Object.entries(selectedDays)
      .filter(([_, isSelected]) => isSelected)
      .map(([dayKey]) => {
        const targetDayIndex = dayNameMap.indexOf(dayKey);
        let dayOffset;
        
        if (targetDayIndex >= originalDayIndex) {
          dayOffset = targetDayIndex - originalDayIndex;
        } else {
          dayOffset = (7 - originalDayIndex) + targetDayIndex;
        }
        
        const targetDate = new Date(originalDate);
        targetDate.setDate(targetDate.getDate() + dayOffset);
        
        return {
          dayName: dayKey.charAt(0).toUpperCase() + dayKey.slice(1),
          date: targetDate,
          isOriginalDay: dayKey === dayNameMap[originalDayIndex]
        };
      })
      .sort((a, b) => a.date - b.date);
  };

  // Generate calendar days
  useEffect(() => {
    const daysArray = [];
    const daysInMonth = getDaysInMonth(selectedYear, selectedMonth);
    
    // Find the first day of the month
    const firstDayOfMonth = new Date(selectedYear, selectedMonth, 1);
    const firstDayOfWeek = firstDayOfMonth.getDay();
    
    // Calculate days from previous month to show (start from Monday)
    let daysFromPreviousMonth;
    if (firstDayOfWeek === 0) {
      daysFromPreviousMonth = 6;
    } else {
      daysFromPreviousMonth = firstDayOfWeek - 1;
    }
    
    // Always show 6 weeks for consistency
    const totalCells = 42;
    
    // Start from the first Monday before the month starts
    const startDate = new Date(firstDayOfMonth);
    startDate.setDate(startDate.getDate() - daysFromPreviousMonth);
    
    // Generate cells for the calendar grid
    for (let i = 0; i < totalCells; i++) {
      const currentDate = new Date(startDate);
      currentDate.setDate(startDate.getDate() + i);
      
      const dateString = getLocalDateString(currentDate);
      const dayOfWeek = currentDate.getDay();
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
      
      // Check if the date is in the current month
      const isInMonth = currentDate.getMonth() === selectedMonth && 
                       currentDate.getFullYear() === selectedYear;
      
      // Check if today
      const today = new Date();
      const todayDateString = getLocalDateString(today);
      const isToday = dateString === todayDateString;
      
      daysArray.push({
        dayNumber: currentDate.getDate(),
        date: dateString,
        isInMonth: isInMonth,
        isWeekend: isWeekend,
        isToday: isToday,
        dayName: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][dayOfWeek],
        dateObj: new Date(currentDate)
      });
    }
    
    setCalendarDays(daysArray);
  }, [selectedYear, selectedMonth]);

  // Initialize user's job title and ward in form data
  useEffect(() => {
    if (userInfo) {
      setFormData(prev => ({
        ...prev,
        categoryOfStaff: userInfo.jobtitle || '',
        wardDepartmentArea: userInfo.ward || ''
      }));
    }
  }, [userInfo]);

  // Handle layout
  useEffect(() => {
    setFluid(true);
    return () => {
      setFluid(false);
    };
  }, [setFluid]);

  // Navigation functions
  const navigateMonth = (direction) => {
    let newYear = selectedYear;
    let newMonth = selectedMonth + direction;
    
    if (newMonth < 0) {
      newMonth = 11;
      newYear -= 1;
    } else if (newMonth > 11) {
      newMonth = 0;
      newYear += 1;
    }
    
    setSelectedYear(newYear);
    setSelectedMonth(newMonth);
  };

  const goToCurrentMonth = () => {
    const current = new Date();
    setSelectedYear(current.getFullYear());
    setSelectedMonth(current.getMonth());
  };

  // Handle shift preset selection
 // ─── DST patch for StaffAvailabilityViewStaff.jsx ────────────────────────────
//
// Root cause: new Date("2026-03-30") parses a date-only string as UTC midnight.
// In IST (UTC+1, active from last Sunday of March) that is 23:00 the previous
// day locally. Every subsequent .setHours() / .toISOString() operation is then
// off by one hour.
//
// Fix: never route a YYYY-MM-DD string through new Date().
// Instead, build datetime strings directly from the date part + time part,
// or construct dates with new Date(year, month, day) which uses LOCAL midnight.
//
// Apply the three replacements below to StaffAvailabilityViewStaff.jsx.
// Everything else in the file stays the same.
// ─────────────────────────────────────────────────────────────────────────────
 
 
// ══ FIX 1 — handlePresetChange ════════════════════════════════════════════════
//
// BEFORE:
//   const selectedDate = new Date(selectedDay.date);
//   const [startHours, startMinutes] = preset.start.split(':').map(Number);
//   const startDateTime = new Date(selectedDate);
//   startDateTime.setHours(startHours, startMinutes, 0, 0);
//   ... (endDateTime similarly) ...
//   setFormData(prev => ({
//     ...prev,
//     startDateTime: startDateTime.toISOString().slice(0, 16),
//     endDateTime:   endDateTime.toISOString().slice(0, 16)
//   }));
//
// AFTER — build the datetime-local string directly from date + preset time:
 
const handlePresetChange = (presetKey) => {
  setSelectedPreset(presetKey);
 
  if (!presetKey || !SHIFT_PRESETS[presetKey] || !selectedDay?.date) return;
 
  const preset   = SHIFT_PRESETS[presetKey];
  const dateStr  = selectedDay.date; // already "YYYY-MM-DD" — local calendar date
 
  // Build ISO-like strings that datetime-local inputs expect: "YYYY-MM-DDTHH:MM"
  // No Date object involved, so no UTC conversion, no DST shift.
  const startStr = `${dateStr}T${preset.start}`;
 
  // Handle midnight-crossing shifts (e.g. ND: 20:00–08:00)
  const [startH] = preset.start.split(':').map(Number);
  const [endH]   = preset.end.split(':').map(Number);
  const crossesMidnight = endH < startH;
 
  let endDateStr = dateStr;
  if (crossesMidnight) {
    // Increment date by 1 using local integers — avoids UTC parse
    const [y, m, d] = dateStr.split('-').map(Number);
    const next = new Date(y, m - 1, d + 1); // local midnight of next day
    const ny   = next.getFullYear();
    const nm   = String(next.getMonth() + 1).padStart(2, '0');
    const nd   = String(next.getDate()).padStart(2, '0');
    endDateStr = `${ny}-${nm}-${nd}`;
  }
  const endStr = `${endDateStr}T${preset.end}`;
 
  setFormData(prev => ({
    ...prev,
    startDateTime: startStr,
    endDateTime:   endStr,
  }));
};
 
 
// ══ FIX 2 — handleDayClick ════════════════════════════════════════════════════
//
// BEFORE:
//   const selectedDate = new Date(day.date);   // ← UTC parse, wrong in DST
//   const defaultStart = new Date(selectedDate);
//   defaultStart.setHours(9, 0, 0, 0);
//   const defaultEnd = new Date(selectedDate);
//   defaultEnd.setHours(17, 0, 0, 0);
//   ...
//   setFormData({
//     startDateTime: defaultStart.toISOString().slice(0, 16),
//     endDateTime:   defaultEnd.toISOString().slice(0, 16),
//     ...
//   });
//
// AFTER — compose strings directly; only fall through to Date arithmetic when
// we need "current time + 1 hour" for today's date:
 
const handleDayClick = (day) => {
  if (!day.isInMonth || !day.date) return;
 
  setSelectedDay(day);
  setSelectedPreset('');
 
  const dayName = getDayNameFromDate(day.date);
  const resetDays = {
    monday: false, tuesday: false, wednesday: false,
    thursday: false, friday: false, saturday: false, sunday: false,
  };
  if (dayName) resetDays[dayName] = true;
  setSelectedDays(resetDays);
 
  const dateStr       = day.date;           // "YYYY-MM-DD" — local calendar date
  const todayLocalStr = getLocalDateString(new Date());
  const isToday       = dateStr === todayLocalStr;
 
  let startStr = `${dateStr}T09:00`;
  let endStr   = `${dateStr}T17:00`;
 
  if (isToday) {
    const now = new Date();
    if (now.getHours() >= 9) {
      // Use current local time + 1 hour.
      // new Date() here is the CURRENT moment, not a date-string parse — safe.
      const hh   = String(now.getHours()).padStart(2, '0');
      const mm   = String(now.getMinutes()).padStart(2, '0');
      const endH = String(now.getHours() + 1).padStart(2, '0');
      startStr = `${dateStr}T${hh}:${mm}`;
      endStr   = `${dateStr}T${endH}:${mm}`;
    }
  }
 
  setFormData({
    startDateTime:       startStr,
    endDateTime:         endStr,
    comments:            '',
    categoryOfStaff:     userInfo?.jobtitle || '',
    wardDepartmentArea:  userInfo?.ward     || '',
    isUnavailable:       false,
    unavailabilityReason:'',
    unavailabilityType:  'Full Day',
    unavailabilityDetails:'',
    isSickLeave:         false,
    sickLeaveType:       'Full Day',
    sicknessReason:      '',
  });
 
  setEditMode(false);
  setEditingAvailability(null);
  setDialogOpen(true);
};
 

  // Auto-set end date based on start date
 const handleStartDateChange = (e) => {
  const { value } = e.target;
  const [datePart, timePart] = value.split('T');
  const [startH, startM] = timePart.split(':').map(Number);

  setFormData(prev => {
    let endStr;
    if (!prev.endDateTime || prev.endDateTime <= value) {
      const endH = startH + 8;
      if (endH >= 24) {
        const [y, mo, d] = datePart.split('-').map(Number);
        const next = new Date(Date.UTC(y, mo - 1, d));
        next.setUTCDate(next.getUTCDate() + 1);
        const nextStr = next.toISOString().split('T')[0];
        endStr = `${nextStr}T${String(endH - 24).padStart(2, '0')}:${String(startM).padStart(2, '0')}`;
      } else {
        endStr = `${datePart}T${String(endH).padStart(2, '0')}:${String(startM).padStart(2, '0')}`;
      }
    } else {
      const endTimePart = prev.endDateTime.split('T')[1];
      endStr = `${datePart}T${endTimePart}`;
    }
    return { ...prev, startDateTime: value, endDateTime: endStr };
  });

  setSelectedPreset('');
};



  // Handle end date change
  const handleEndDateChange = (e) => {
    const { value } = e.target;
    setFormData(prev => ({
      ...prev,
      endDateTime: value
    }));
    setSelectedPreset('');
  };

  // Handle form field changes
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    if (name === 'startDateTime') {
      handleStartDateChange(e);
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  

  // Handle edit click
  const handleEditClick = (availability, event) => {
    event.stopPropagation();
    
    setEditingAvailability(availability);
    setEditMode(true);
    setSelectedPreset('');
    
    // Format dates for datetime-local input
    
    
    setFormData({
      startDateTime: dublinDateTimeInputStr(availability.startDateTime),
      endDateTime: dublinDateTimeInputStr(availability.endDateTime),
      comments: availability.comments || '',
      categoryOfStaff: availability.categoryOfStaff || userInfo?.jobtitle || '',
      wardDepartmentArea: availability.wardDepartmentArea || userInfo?.ward || '',
      isUnavailable: availability.isUnavailable || availability.isSickLeave || false,
      unavailabilityReason: availability.unavailabilityReason || availability.sicknessReason || '',
      unavailabilityType: availability.unavailabilityType || availability.sickLeaveType || 'Full Day',
      unavailabilityDetails: availability.unavailabilityDetails || '',
      isSickLeave: availability.isSickLeave || false,
      sickLeaveType: availability.sickLeaveType || 'Full Day',
      sicknessReason: availability.sicknessReason || ''
    });
    
    // Find the day this availability belongs to
    const availabilityDate = new Date(availability.startDateTime);
    const dateString = getLocalDateString(availabilityDate.toISOString());
    
    const dayForAvailability = calendarDays.find(day => day.date === dateString);
    if (dayForAvailability) {
      setSelectedDay(dayForAvailability);
    }
    
    setDialogOpen(true);
  };

  // Handle delete confirmation
  const handleDeleteConfirmOpen = () => {
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirmClose = () => {
    setDeleteConfirmOpen(false);
  };

  const handleConfirmDelete = async () => {
    if (!editingAvailability) return;
    
    try {
      await updateAvailability({
        id: editingAvailability._id,
        isActive: false
      }).unwrap();
      
      showSnackbar('Availability removed successfully', 'success');
      refetchAvailabilities();
      
      setDeleteConfirmOpen(false);
      setDialogOpen(false);
      setEditMode(false);
      setEditingAvailability(null);
    } catch (error) {
      showSnackbar(error?.data?.message || 'Failed to remove availability', 'error');
      setDeleteConfirmOpen(false);
    }
  };

  // Handle soft delete
  const handleDeleteClick = async (availabilityId, event = null) => {
    if (event) {
      event.stopPropagation();
    }
    
    if (window.confirm('Are you sure you want to remove this availability?')) {
      try {
        await updateAvailability({
          id: availabilityId,
          isActive: false
        }).unwrap();
        
        showSnackbar('Availability removed successfully', 'success');
        refetchAvailabilities();
        
        if (editMode) {
          setDialogOpen(false);
          setEditMode(false);
          setEditingAvailability(null);
        }
      } catch (error) {
        showSnackbar(error?.data?.message || 'Failed to remove availability', 'error');
      }
    }
  };

  // Submit single availability
  const submitSingleAvailability = async (startDate, endDate, isEditMode = false) => {
    const startDateTimeISO = startDate.toISOString();
    const endDateTimeISO = endDate.toISOString();
    
    const submissionData = {
      ...formData,
      startDateTime: startDateTimeISO,
      endDateTime: endDateTimeISO,
      requestedBy: userInfo._id,
      requestedByName: userInfo.name,
      requestedByEmail: userInfo.email
    };
    
    if (isEditMode && editingAvailability) {
      if (startDateTimeISO === editingAvailability.startDateTime) {
        return await updateAvailability({
          id: editingAvailability._id,
          ...submissionData
        }).unwrap();
      }
      return await createAvailability(submissionData).unwrap();
    } else {
      return await createAvailability(submissionData).unwrap();
    }
  };

  // Handle form submission
  const handleFormSubmit = async () => {
    try {
      if (!formData.startDateTime || !formData.endDateTime) {
        showSnackbar('Start and end times are required', 'error');
        return;
      }
      
      const [startDatePart, startTimePart] = formData.startDateTime.split('T');
      const [endDatePart, endTimePart] = formData.endDateTime.split('T');
      const startDateTimeISO = dublinLocalToUtcISO(startDatePart, startTimePart);
      const endDateTimeISO = dublinLocalToUtcISO(endDatePart, endTimePart);
      const startTime = new Date(startDateTimeISO);
      const endTime = new Date(endDateTimeISO);
      
      if (endTime <= startTime) {
        showSnackbar('End time must be after start time', 'error');
        return;
      }
      
      const durationHours = (endTime - startTime) / (1000 * 60 * 60);
      if (durationHours > 24) {
        showSnackbar('Availability cannot exceed 24 hours', 'error');
        return;
      }
      
      const selectedDayKeys = Object.entries(selectedDays)
        .filter(([_, isSelected]) => isSelected)
        .map(([dayKey]) => dayKey);
      
      if (selectedDayKeys.length === 0) {
        await submitSingleAvailability(startTime, endTime, editMode);
        showSnackbar('Availability saved successfully', 'success');
        
        setDialogOpen(false);
        refetchAvailabilities();
        setSelectedPreset('');
        return;
      }
      
      const originalStartDate = new Date(formData.startDateTime);
      const originalDayIndex = originalStartDate.getDay();
      const dayNameMap = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
      const originalDayName = dayNameMap[originalDayIndex];
      
      const promises = [];
      const successMessages = [];
      
      if (selectedDays[originalDayName]) {
        promises.push(
          submitSingleAvailability(startTime, endTime, editMode)
            .then(() => {
              successMessages.push(`${originalDayName.charAt(0).toUpperCase() + originalDayName.slice(1)}: Saved`);
            })
        );
      }
      
      for (const dayKey of selectedDayKeys) {
        if (dayKey === originalDayName) continue;
        
        const targetDayIndex = dayNameMap.indexOf(dayKey);
        let dayOffset;
        
        if (targetDayIndex >= originalDayIndex) {
          dayOffset = targetDayIndex - originalDayIndex;
        } else {
          dayOffset = (7 - originalDayIndex) + targetDayIndex;
        }
        
        const newStartDate = new Date(startTime);
        newStartDate.setDate(newStartDate.getDate() + dayOffset);
        
        const newEndDate = new Date(endTime);
        newEndDate.setDate(newEndDate.getDate() + dayOffset);
        
        promises.push(
          submitSingleAvailability(newStartDate, newEndDate, false)
            .then(() => {
              successMessages.push(`${dayKey.charAt(0).toUpperCase() + dayKey.slice(1)}: Saved`);
            })
        );
      }
      
      await Promise.all(promises);
      
      const savedDaysInfo = getSelectedDatesInfo();
      const savedDaysText = savedDaysInfo
        .map(info => `${info.dayName} ${info.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`)
        .join(', ');
      
      showSnackbar(`Availability saved for: ${savedDaysText}`, 'success');
      
      setDialogOpen(false);
      refetchAvailabilities();
      setSelectedPreset('');
      
    } catch (error) {
      console.error('Form submit error:', error);
      showSnackbar(error?.data?.message || 'Failed to save availability', 'error');
    }
  };

  // Handle dialog close
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditMode(false);
    setEditingAvailability(null);
    setSelectedPreset('');
    setSelectedDays({
      monday: false,
      tuesday: false,
      wednesday: false,
      thursday: false,
      friday: false,
      saturday: false,
      sunday: false
    });
  };

  // Show snackbar
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Get availabilities for a specific day
  const getDayAvailabilities = (date) => {
    if (!availabilitiesData?.availabilities || !userInfo) {
      return [];
    }
    
    const dateObj = new Date(date);
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    const formattedDate = `${year}-${month}-${day}`;
    
    return availabilitiesData.availabilities[formattedDate] || [];
  };

  // Drag and drop handlers
  const handleDragStart = (availability, event) => {
    setDraggingAvailability(availability);
    event.dataTransfer.setData('text/plain', JSON.stringify(availability));
    event.dataTransfer.effectAllowed = 'move';
    event.currentTarget.style.opacity = '0.5';
  };

  const handleDragEnd = (event) => {
    setDraggingAvailability(null);
    setDragOverDay(null);
    event.currentTarget.style.opacity = '1';
  };

  const handleDragOver = (day, event) => {
    event.preventDefault();
    if (day.isInMonth && day.date) {
      setDragOverDay(day);
    }
  };

  const handleDragLeave = () => {
    setDragOverDay(null);
  };

  // ══ FIX 3 — handleDrop ════════════════════════════════════════════════════════
//
// BEFORE (inside handleDrop):
//   const targetDate = new Date(day.date);          // ← UTC parse
//   const newStartDate = new Date(targetDate);
//   newStartDate.setHours(originalStartTime.hours, originalStartTime.minutes, 0, 0);
//   ...
//   const formatForForm = (date) => { ... date.getFullYear() ... }  ← this part is fine
//
// AFTER — build targetDate from local integers so .setHours() acts on local midnight:
 
const handleDrop = async (day, event) => {
  event.preventDefault();
  setDragOverDay(null);
 
  if (!day.isInMonth || !day.date || !draggingAvailability) return;
 
  try {
    const data             = event.dataTransfer.getData('text/plain');
    const availabilityData = JSON.parse(data);
 
    setSelectedDay(day);
    setEditingAvailability(availabilityData);
    setEditMode(true);
 
    const originalStart = new Date(availabilityData.startDateTime);
    const originalEnd   = new Date(availabilityData.endDateTime);
 
    // Build target date from LOCAL integers — not from a date-string UTC parse
    const [y, m, d]  = day.date.split('-').map(Number);
    const targetDate = new Date(y, m - 1, d); // local midnight ✅
 
    const newStartDate = new Date(targetDate);
    newStartDate.setHours(originalStart.getHours(), originalStart.getMinutes(), 0, 0);
 
    const newEndDate = new Date(targetDate);
    newEndDate.setHours(originalEnd.getHours(), originalEnd.getMinutes(), 0, 0);
 
    // Preserve overnight shifts
    const originalStartDateStr = originalStart.toISOString().split('T')[0];
    const originalEndDateStr   = originalEnd.toISOString().split('T')[0];
    if (originalEndDateStr !== originalStartDateStr) {
      newEndDate.setDate(newEndDate.getDate() + 1);
    }
 
    // formatForForm is unchanged — getFullYear/getMonth/getDate read LOCAL values ✅
    const formatForForm = (date) => {
      const yr  = date.getFullYear();
      const mo  = String(date.getMonth() + 1).padStart(2, '0');
      const dy  = String(date.getDate()).padStart(2, '0');
      const hr  = String(date.getHours()).padStart(2, '0');
      const min = String(date.getMinutes()).padStart(2, '0');
      return `${yr}-${mo}-${dy}T${hr}:${min}`;
    };
 
    setFormData({
      startDateTime:        formatForForm(newStartDate),
      endDateTime:          formatForForm(newEndDate),
      comments:             availabilityData.comments             || '',
      categoryOfStaff:      availabilityData.categoryOfStaff      || userInfo?.jobtitle || '',
      wardDepartmentArea:   availabilityData.wardDepartmentArea   || userInfo?.ward     || '',
      isUnavailable:        availabilityData.isUnavailable        || availabilityData.isSickLeave || false,
      unavailabilityReason: availabilityData.unavailabilityReason || availabilityData.sicknessReason || '',
      unavailabilityType:   availabilityData.unavailabilityType   || availabilityData.sickLeaveType  || 'Full Day',
      unavailabilityDetails:availabilityData.unavailabilityDetails || '',
      isSickLeave:          availabilityData.isSickLeave          || false,
      sickLeaveType:        availabilityData.sickLeaveType        || 'Full Day',
      sicknessReason:       availabilityData.sicknessReason       || '',
    });
 
    setDialogOpen(true);
    showSnackbar('Edit availability with new date. Please review and save.', 'info');
 
  } catch (error) {
    console.error('Error preparing move:', error);
    showSnackbar('Error preparing availability move', 'error');
  } finally {
    setDraggingAvailability(null);
  }
};
 
 
// ══ ALSO CHECK — handleStartDateChange ═══════════════════════════════════════
//
// This function receives the raw value from a datetime-local input, e.g.
// "2026-03-30T08:00". new Date("2026-03-30T08:00") WITHOUT a timezone suffix
// is parsed as LOCAL time by browsers (per HTML spec for datetime-local), so
// this one is actually safe. No change needed here.
//
// The .toISOString().slice(0, 16) at the end DOES convert to UTC, but since
// the value goes straight back into a datetime-local input that also reads it
// as local, the round-trip is consistent. Only a problem if you send it to the
// server as-is — but your submitSingleAvailability calls new Date(formData.startDateTime)
// which browser-parses as local, then .toISOString() gives correct UTC. ✅
 


  // Format day display
  const formatDayDisplay = (day) => {
    if (!day.isInMonth) {
      return null;
    }
    
    const dayAvailabilities = getDayAvailabilities(day.date);
    const hasAvailabilities = dayAvailabilities.length > 0;
    
    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 0.5,
          p: 0.5,
          bgcolor: day.isWeekend ? 'rgba(255, 152, 0, 0.3)' : 
                  day.isToday ? 'rgba(111, 153, 245, 0.3)' : 'rgba(99, 100, 102, .3)',
          borderBottom: 1,
          borderColor: 'divider'
        }}>
          <Typography variant="caption" fontWeight="bold">
            {day.dayNumber} {day.dayName}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {day.isToday && (
              <Chip 
                label="Today" 
                size="small" 
                sx={{ 
                  height: 16, 
                  fontSize: '0.6rem',
                  bgcolor: 'info.light',
                  color: 'info.contrastText'
                }} 
              />
            )}
            {day.isWeekend && (
              <Chip 
                label="WE" 
                size="small" 
                sx={{ 
                  height: 16, 
                  fontSize: '0.6rem',
                  bgcolor: 'warning.light',
                  color: 'warning.contrastText'
                }} 
              />
            )}
          </Box>
        </Box>
        
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5, p: 0.5, bgcolor: 'rgba(238, 238, 165, 0.33)' }}>
          {hasAvailabilities ? (
            <Box sx={{ maxHeight: '80px', overflow: 'auto' }}>
              {dayAvailabilities.map((availability) => (
                <Paper
                  key={availability._id}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditClick(availability, e);
                  }}
                  sx={{ 
                    p: 0.5, 
                    mb: 0.5,
                    borderRadius: 0.5,
                    bgcolor: (availability.isUnavailable || availability.isSickLeave) ? 'error.light' :
                            availability.status === 'Approved' ? 'success.light' :
                            availability.status === 'Rejected' ? 'error.light' : 'info.light',
                    color: (availability.isUnavailable || availability.isSickLeave) ? 'error.contrastText' :
                          availability.status === 'Approved' ? 'success.contrastText' :
                          availability.status === 'Rejected' ? 'error.contrastText' : 'info.contrastText',
                    cursor: 'pointer',
                    '&:hover': {
                      boxShadow: 2,
                      transform: 'translateY(-1px)',
                      transition: 'all 0.2s'
                    },
                    borderLeft: (availability.isUnavailable || availability.isSickLeave) ? '3px solid #d32f2f' : 'none'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {(availability.isUnavailable || availability.isSickLeave) ? (
                      <AccessTime fontSize="small" sx={{ color: '#d32f2f' }} />
                    ) : (
                      <AccessTime fontSize="small" sx={{ opacity: 0.7 }} />
                    )}
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" sx={{ fontSize: '0.7rem', display: 'block' }}>
                        {formatTime(availability.startDateTime)} - {formatTime(availability.endDateTime)}
                        {(availability.isUnavailable || availability.isSickLeave) && (
                          <Chip 
                            label="Unavailable" 
                            size="small" 
                            sx={{ 
                              ml: 1,
                              height: 14,
                              fontSize: '0.55rem',
                              bgcolor: '#d32f2f',
                              color: 'white'
                            }} 
                          />
                        )}
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.6rem', opacity: 0.9 }}>
                        {formatHoursMinutes(availability.durationMinutes)}
                        {(availability.unavailabilityReason || availability.sicknessReason) && (
                          <span style={{ marginLeft: '4px', fontStyle: 'italic' }}>
                            • {availability.unavailabilityReason || availability.sicknessReason}
                          </span>
                        )}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <MuiIconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditClick(availability, e);
                        }}
                        sx={{ 
                          color: 'inherit',
                          p: 0.25,
                          '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.2)' }
                        }}
                      >
                        <Edit fontSize="small" />
                      </MuiIconButton>
                    </Box>
                  </Box>
                </Paper>
              ))}
            </Box>
          ) : (
            <Box sx={{ 
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              p: 1
            }}>
              <Typography variant="caption" color="text.disabled" sx={{ textAlign: 'center' }}>
                No availability set
              </Typography>
              <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.6rem', textAlign: 'center' }}>
                Click to add
              </Typography>
            </Box>
          )}
          
          {hasAvailabilities && (
            <Box sx={{ 
              mt: 'auto',
              pt: 0.5,
              borderTop: 1,
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>
                Total
              </Typography>
              <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.65rem' }}>
                {formatHoursMinutes(dayAvailabilities.reduce((sum, a) => sum + a.durationMinutes, 0))}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  const isLoading = loadingAvailabilities || creating || updating;
  
  if (isLoading && !availabilitiesData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box sx={{ p: 3 }}>
      {/* Navigation Breadcrumbs */}
      <Breadcrumbs 
        aria-label="breadcrumb" 
        separator={<NavigateNext fontSize="small" />}
        sx={{ mb: 2 }}
      >
        <Link
          underline="hover"
          color="inherit"
          href="#"
          onClick={(e) => {
            e.preventDefault();
            navigate('/');
          }}
        >
          Dashboard
        </Link>
        <Typography color="text.primary">
          My Availability
        </Typography>
      </Breadcrumbs>
      
     {/* Header */}
<Box sx={{
  display: 'flex',
  flexDirection: { xs: 'column', sm: 'row' },   // stack on mobile
  justifyContent: 'space-between',
  alignItems: { xs: 'flex-start', sm: 'center' },
  gap: 1,
  mb: 3,
}}>
  <Box>
    <Typography
      variant="h4"
      sx={{
        mb: 0,
        fontSize: { xs: '1.2rem', sm: '1.75rem', md: '2.125rem' }, // smaller on mobile
      }}
    >
      My Availability
    </Typography>
    <Typography
      variant="subtitle2"
      color="primary"
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',          // wraps if name+jobtitle is long
        gap: 0.5,
        fontSize: { xs: '0.75rem', sm: '0.875rem' },
      }}
    >
      <LocalHospital fontSize="small" />
      {/* On mobile show name only, on sm+ show all three */}
      <span>{userInfo?.name}</span>
      <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
        • {userInfo?.jobtitle} • {userInfo?.ward}
      </Box>
    </Typography>
  </Box>

  <Button
    variant="outlined"
    size="small"                                  // smaller button
    startIcon={<CalendarToday fontSize="small" />}
    onClick={() => navigate('/')}
    sx={{
      fontSize: { xs: '0.7rem', sm: '0.875rem' },
      whiteSpace: 'nowrap',
      alignSelf: { xs: 'flex-end', sm: 'center' }, // push to right on mobile
    }}
  >
    {/* Shorter label on mobile */}
    <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
      Back to Main Menu
    </Box>
    <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
      Menu
    </Box>
  </Button>
</Box>
      
      {/* Month Selection */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 2, bgcolor: '#f5f5f5' }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Month Selection
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton onClick={() => navigateMonth(-1)} size="small">
                  <ArrowBackIos fontSize="small" />
                </IconButton>
                <Typography variant="h6">
                  {monthInfo.monthName} {monthInfo.year}
                </Typography>
                <IconButton onClick={() => navigateMonth(1)} size="small">
                  <ArrowForwardIos fontSize="small" />
                </IconButton>
              </Box>
              
              <Button 
                startIcon={<Today />} 
                onClick={goToCurrentMonth}
                variant="outlined"
                size="small"
              >
                Current Month
              </Button>
            </Box>
            
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Year</InputLabel>
                  <Select
                    value={selectedYear}
                    label="Year"
                    onChange={(e) => setSelectedYear(e.target.value)}
                  >
                    {generateYears().map(year => (
                      <MenuItem key={year} value={year}>{year}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Month</InputLabel>
                  <Select
                    value={selectedMonth}
                    label="Month"
                    onChange={(e) => setSelectedMonth(e.target.value)}
                  >
                    {monthNames.map((month, index) => (
                      <MenuItem key={month} value={index}>{month}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Box sx={{ p: 1, bgcolor: 'white', borderRadius: 1 }}>
                  <Typography variant="body2" color="textSecondary">
                    <strong>Total Hours:</strong> {formatHoursMinutes(
                      availabilitiesData?.availabilities 
                        ? Object.values(availabilitiesData.availabilities).flat()
                            .reduce((sum, a) => sum + a.durationMinutes, 0)
                        : 0
                    )}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
      
      {/* Calendar Grid */}
      <Paper elevation={3} sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          My Availability.. Calendar - {monthInfo.monthName} {monthInfo.year}
        </Typography>
        
        {/* Weekday Headers */}
        <Grid container spacing={1} sx={{ mb: 1 }}>
          {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day) => (
            <Grid item xs key={day}>
              <Paper 
                elevation={0}
                sx={{ 
                  p: 1, 
                  textAlign: 'center',
                  bgcolor: day === 'Saturday' || day === 'Sunday' ? 'rgba(255, 152, 0, 0.1)' : 'rgba(0, 0, 0, 0.04)',
                  borderBottom: '2px solid',
                  borderColor: day === 'Saturday' || day === 'Sunday' ? 'warning.light' : 'primary.light'
                }}
              >
                <Typography variant="subtitle2" fontWeight="bold">
                  {day.substring(0, 3)}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
        
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            Click on any day cell to add your available hours. You can add multiple time slots per day.
            Drag availability blocks between days to move them. Approved availabilities are shown in green.
            <strong> Unavailable periods are shown in red.</strong>
          </Typography>
        </Alert>
        
        {/* SCROLLABLE CONTAINER */}
        <Box sx={scrollableTableStyle}>
          {(() => {
            const weeks = [];
            for (let i = 0; i < calendarDays.length; i += 7) {
              weeks.push(calendarDays.slice(i, i + 7));
            }
            
            return weeks.map((week, weekIndex) => (
              <Grid container spacing={1} key={weekIndex} sx={{ mb: 1 }}>
                {week.map((day) => (
                  <Grid item xs key={day.date || `empty-${day.dayNumber}`}>
                    <Paper 
                      elevation={1}
                      sx={{ 
                        height: '140px',
                        border: 1,
                        borderColor: day.isInMonth ? 
                          (day.isWeekend ? 'warning.light' : 
                           day.isToday ? 'info.light' : 'divider') : 
                          'grey.300',
                        bgcolor: day.isInMonth ? 
                          (day.isWeekend ? 'rgba(255, 152, 0, 0.05)' : 
                           day.isToday ? 'rgba(33, 150, 243, 0.05)' : 'white') : 
                          'rgba(33, 150, 243, 0.05)',
                        overflow: 'hidden',
                        position: 'relative',
                        cursor: day.isInMonth ? 'pointer' : 'default',
                        opacity: day.isInMonth ? 1 : 0.7,
                        '&:hover': day.isInMonth ? {
                          boxShadow: 3,
                          transform: 'translateY(-2px)',
                          transition: 'all 0.2s'
                        } : {}
                      }}
                      onClick={() => {
                        if (day.isInMonth && day.date) {
                          handleDayClick(day);
                        }
                      }}
                      onDragOver={(e) => handleDragOver(day, e)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(day, e)}
                    >
                      {formatDayDisplay(day)}
                      
                      <Box sx={{ 
                        position: 'absolute',
                        top: 2,
                        left: 2,
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        bgcolor: !day.isInMonth ? 'grey.400' :
                                day.isWeekend ? 'warning.main' : 
                                day.isToday ? 'info.main' : 'primary.main',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <Typography variant="caption" fontWeight="bold">
                          {day.dayNumber}
                        </Typography>
                      </Box>
                      
                      {!day.isInMonth && (
                        <Box sx={{ 
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          fontSize: '0.7rem',
                          color: 'text.disabled',
                          fontStyle: 'italic',
                          textAlign: 'center'
                        }}>
                          <Typography variant="caption" display="block">
                            {new Date(day.date).toLocaleDateString('en-US', { month: 'short' })}
                          </Typography>
                        </Box>
                      )}
                      
                      {day.isInMonth && day.date && getDayAvailabilities(day.date).length === 0 && (
                        <Box sx={{ 
                          position: 'absolute',
                          top: 2,
                          right: 2,
                          width: 20,
                          height: 20,
                          borderRadius: '50%',
                          bgcolor: 'grey.300',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <Add fontSize="small" />
                        </Box>
                      )}
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            ));
          })()}
        </Box>
        
        {/* Legend */}
        <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="subtitle2" gutterBottom>
            Color Legend:
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 16, height: 16, bgcolor: 'info.light', borderRadius: 1 }} />
                  <Typography variant="body2">Pending Availability</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 16, height: 16, bgcolor: 'success.light', borderRadius: 1 }} />
                  <Typography variant="body2">Approved Availability</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 16, height: 16, bgcolor: 'error.light', borderRadius: 1 }} />
                  <Typography variant="body2">Rejected Availability</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 16, height: 16, bgcolor: 'warning.light', borderRadius: 1 }} />
                  <Typography variant="body2">Weekend Days</Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: 'white', borderRadius: 1 }}>
                <Box sx={{ 
                  width: 24, 
                  height: 24, 
                  borderRadius: '50%',
                  bgcolor: 'grey.300',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Add fontSize="small" />
                </Box>
                <Typography variant="body2">
                  Click on any day cell to add/update your availability
                </Typography>
              </Box>
              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: 'white', borderRadius: 1 }}>
                <DragIndicator color="action" />
                <Typography variant="body2">
                  Drag availability blocks between days to move them
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={handleDeleteConfirmClose} maxWidth="sm" fullWidth>
        <DialogTitle>Remove Availability</DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            Are you sure you want to remove this availability?
          </Typography>
          {editingAvailability && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Time:</strong> {formatTime(editingAvailability.startDateTime)} - {formatTime(editingAvailability.endDateTime)}
              </Typography>
              <Typography variant="body2">
                <strong>Date:</strong> {formatDate(editingAvailability.startDateTime)}
              </Typography>
            </Box>
          )}
          <Typography variant="body2" color="textSecondary" sx={{ mt: 2, fontStyle: 'italic' }}>
            Note: This action can be reversed by an administrator.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteConfirmClose}>Cancel</Button>
          <Button 
            onClick={handleConfirmDelete} 
            variant="contained" 
            color="error"
            startIcon={<Delete />}
          >
            Remove Availability
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Add/Edit Availability Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editMode ? 'Edit Availability' : 'Add Availability'}
          <Typography variant="subtitle2" color="textSecondary">
            {selectedDay && formatDate(selectedDay.date)}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {/* Shift Presets - Optimized for mobile */}
            <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WatchLater fontSize="small" />
                Quick Shift Presets
              </Typography>
              <Grid container spacing={1}>
                {Object.entries(SHIFT_PRESETS).map(([key, preset]) => (
                  <Grid item xs={12} sm={6} md={4} key={key}>
                    <Button
                      fullWidth
                      variant={selectedPreset === key ? "contained" : "outlined"}
                      size="small"
                      onClick={() => handlePresetChange(key)}
                      sx={{ 
                        justifyContent: 'flex-start',
                        textAlign: 'left',
                        whiteSpace: 'normal',
                        fontSize: '0.75rem',
                        py: 1
                      }}
                    >
                      {preset.label}
                    </Button>
                  </Grid>
                ))}
              </Grid>
            </Box>
            
            {/* Multiple Day Selection */}
            <Box sx={{ mb: 3, p: 2, bgcolor: '#e3f2fd', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarToday fontSize="small" />
                Apply to Multiple Days
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="caption" color="textSecondary" gutterBottom>
                  Select days of the week to apply the same availability:
                </Typography>
                
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {Object.entries({
                    monday: 'Mon',
                    tuesday: 'Tue',
                    wednesday: 'Wed',
                    thursday: 'Thu',
                    friday: 'Fri',
                    saturday: 'Sat',
                    sunday: 'Sun'
                  }).map(([dayKey, dayLabel]) => {
                    const isOriginalDay = dayKey === getDayNameFromDate(formData.startDateTime)?.toLowerCase();
                    return (
                      <Box key={dayKey} sx={{ display: 'flex', alignItems: 'center' }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={selectedDays[dayKey]}
                              onChange={() => handleDayToggle(dayKey)}
                              disabled={isLoading}
                            />
                          }
                          label={
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                fontWeight: isOriginalDay ? 'bold' : 'normal',
                                color: isOriginalDay ? 'primary.main' : 'text.primary'
                              }}
                            >
                              {dayLabel}
                              {isOriginalDay && ' *'}
                            </Typography>
                          }
                        />
                      </Box>
                    );
                  })}
                </Box>
                
                {/* Display selected days info */}
                {Object.values(selectedDays).some(day => day) && formData.startDateTime && (
                  <Box sx={{ mt: 1, p: 1.5, bgcolor: 'white', borderRadius: 0.5 }}>
                    <Typography variant="caption" fontWeight="medium" gutterBottom>
                      This availability will be applied to:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                      {getSelectedDatesInfo().map(({ dayName, date, isOriginalDay }) => (
                        <Chip
                          key={`${dayName}-${date.toISOString()}`}
                          label={`${dayName} ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}${isOriginalDay ? ' (Original)' : ''}`}
                          size="small"
                          color={isOriginalDay ? "primary" : "default"}
                          variant={isOriginalDay ? "filled" : "outlined"}
                          sx={{ fontSize: '0.7rem', height: 24 }}
                        />
                      ))}
                    </Box>
                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
                      * Indicates the original day
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Start Time"
                  type="datetime-local"
                  name="startDateTime"
                  value={formData.startDateTime}
                  onChange={handleStartDateChange}
                  InputLabelProps={{ shrink: true }}
                  disabled={isLoading}
                  size="small"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="End Time"
                  type="datetime-local"
                  name="endDateTime"
                  value={formData.endDateTime}
                  onChange={handleEndDateChange}
                  InputLabelProps={{ shrink: true }}
                  disabled={isLoading}
                  size="small"
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Comments (Optional)"
                  name="comments"
                  value={formData.comments}
                  onChange={handleFormChange}
                  multiline
                  rows={2}
                  disabled={isLoading}
                  placeholder="Any additional notes about your availability..."
                  size="small"
                />
              </Grid>
              
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isUnavailable}
                      onChange={(e) => {
                        const isUnavailable = e.target.checked;
                        setFormData(prev => ({
                          ...prev,
                          isUnavailable: isUnavailable,
                          // When marking as unavailable, clear sick leave
                          isSickLeave: isUnavailable ? false : prev.isSickLeave,
                          unavailabilityReason: isUnavailable ? prev.unavailabilityReason : '',
                          unavailabilityType: isUnavailable ? 'Full Day' : 'Full Day',
                          unavailabilityDetails: isUnavailable ? prev.unavailabilityDetails : '',
                          status: isUnavailable ? 'Approved' : 'Open'
                        }));
                      }}
                      color="error"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ 
                        width: 16, 
                        height: 16, 
                        bgcolor: 'error.light',
                        borderRadius: '2px',
                        border: '1px solid #d32f2f'
                      }} />
                      <Typography variant="body2" color={formData.isUnavailable ? 'error.main' : 'text.primary'}>
                        Mark as Unavailable (Annual Leave, etc.)
                      </Typography>
                    </Box>
                  }
                />
              </Grid>
              {/*
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isSickLeave}
                      onChange={(e) => {
                        const isSickLeave = e.target.checked;
                        setFormData(prev => ({
                          ...prev,
                          isSickLeave: isSickLeave,
                          // When marking as sick leave, clear unavailability
                          isUnavailable: isSickLeave ? false : prev.isUnavailable,
                          sicknessReason: isSickLeave ? prev.sicknessReason : '',
                          sickLeaveType: isSickLeave ? 'Full Day' : 'Full Day',
                          status: isSickLeave ? 'Approved' : 'Open'
                        }));
                      }}
                      color="warning"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocalHospital fontSize="small" color={formData.isSickLeave ? 'warning' : 'inherit'} />
                      <Typography variant="body2" color={formData.isSickLeave ? 'warning.main' : 'text.primary'}>
                        Mark as Sick Leave
                      </Typography>
                    </Box>
                  }
                />
              </Grid>
              */}
              
              {formData.isUnavailable && (
                <>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Unavailability Reason</InputLabel>
                      <Select
                        value={formData.unavailabilityReason}
                        label="Unavailability Reason"
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          unavailabilityReason: e.target.value
                        }))}
                      >
                        <MenuItem value="">Select Reason...</MenuItem>
                        {UNAVAILABILITY_REASONS.map((reason) => (
                          <MenuItem key={reason} value={reason}>
                            {reason}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Unavailability Type</InputLabel>
                      <Select
                        value={formData.unavailabilityType}
                        label="Unavailability Type"
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          unavailabilityType: e.target.value
                        }))}
                      >
                        <MenuItem value="Full Day">Full Day</MenuItem>
                        <MenuItem value="Half Day AM">Half Day (Morning)</MenuItem>
                        <MenuItem value="Half Day PM">Half Day (Afternoon)</MenuItem>
                        <MenuItem value="Partial Day">Partial Day</MenuItem>
                        <MenuItem value="Multi-Day">Multi-Day</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Additional Details (Optional)"
                      value={formData.unavailabilityDetails}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        unavailabilityDetails: e.target.value
                      }))}
                      multiline
                      rows={2}
                      placeholder="Any additional details..."
                      size="small"
                    />
                  </Grid>
                </>
              )}

              {formData.isSickLeave && (
                <>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Sick Leave Type</InputLabel>
                      <Select
                        value={formData.sickLeaveType}
                        label="Sick Leave Type"
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          sickLeaveType: e.target.value
                        }))}
                      >
                        <MenuItem value="Full Day">Full Day</MenuItem>
                        <MenuItem value="Half Day AM">Half Day (Morning)</MenuItem>
                        <MenuItem value="Half Day PM">Half Day (Afternoon)</MenuItem>
                        <MenuItem value="Partial Day">Partial Day</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Sickness Reason (Optional)"
                      value={formData.sicknessReason}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        sicknessReason: e.target.value
                      }))}
                      placeholder="e.g., Flu, Injury, etc."
                      size="small"
                    />
                  </Grid>
                </>
              )}
              
              {/* Show existing availabilities for this day */}
              {selectedDay && getDayAvailabilities(selectedDay.date).length > 0 && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" gutterBottom>
                    Existing availabilities for this day:
                  </Typography>
                  <List dense>
                    {getDayAvailabilities(selectedDay.date)
                      .filter(a => !editMode || a._id !== editingAvailability?._id)
                      .map((availability) => (
                        <ListItem 
                          key={availability._id}
                          sx={{ 
                            bgcolor: 'grey.50',
                            borderRadius: 1,
                            mb: 0.5
                          }}
                        >
                          <ListItemText
                            primary={`${formatTime(availability.startDateTime)} - ${formatTime(availability.endDateTime)}`}
                            secondary={formatHoursMinutes(availability.durationMinutes)}
                          />
                          <ListItemSecondaryAction>
                            <MuiIconButton
                              size="small"
                              onClick={(e) => handleEditClick(availability, e)}
                              disabled={isLoading}
                            >
                              <Edit fontSize="small" />
                            </MuiIconButton>
                            <MuiIconButton
                              size="small"
                              onClick={(e) => handleDeleteClick(availability._id, e)}
                              disabled={isLoading}
                              sx={{ ml: 0.5 }}
                            >
                              <Delete fontSize="small" />
                            </MuiIconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                  </List>
                </Grid>
              )}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Button onClick={handleCloseDialog} disabled={isLoading}>
            Cancel
          </Button>

          {editMode && editingAvailability && (
            <Button 
              onClick={handleDeleteConfirmOpen}
              variant="outlined" 
              color="error"
              disabled={isLoading}
              startIcon={<Delete />}
              sx={{ mr: 'auto' }}
            >
              Remove
            </Button>
          )}

          <Button 
            onClick={handleFormSubmit} 
            variant="contained" 
            disabled={isLoading}
            startIcon={editMode ? <Edit /> : <Add />}
          >
            {editMode ? 'Update' : 'Add'} Availability
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default StaffAvailabilityViewStaff;